import { getFieldBuilderObjectFactory } from '@/components/form-builder/field-builder/utils/field-builder-factory';
import {
  CustomFieldProps,
  DateFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  TextareaFieldProps,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { Currency, FundingDto, SlotDto } from '@/types';
import { combineLatest, map, Observable, of } from 'rxjs';
import { dateUtils } from '@/utils/date.utils';
import { PpmSlotsFormComponent } from '../slots-form/slots-form.component';
import { PpmMilestoneRepository } from '@/store/ppm-state/milestones-state/milestones.repository';
import { BudgetEstimateComponent } from '@/components/shared/budget-estimate/budget-estimate.component';
import { PpmFundingSourceFormComponent } from '../funding-source-form/funding-source-form.component';

interface getPpmMilestoneFormObjectProps {
  store: PpmMilestoneRepository;
  marketTypesOptions?: SelectOption[];
  financialSourcesOptions?: (SelectOption & { examenPrealable: boolean })[];
  currencyOptions?: SelectOption[];
  selectionModeOptions?: SelectOption[];
  isDisabled?: Observable<boolean>;
  minDate?: Date;
  currencies?: Currency[];
  actualizedCount?: Observable<number>;
}

export const getPpmMilestoneFormObject = ({
  store,
  marketTypesOptions,
  financialSourcesOptions,
  currencyOptions,
  selectionModeOptions,
  isDisabled,
  currencies,
  actualizedCount,
}: getPpmMilestoneFormObjectProps): DynamicForm => {
  const beneficiaryField: DynamicField<TextFieldProps> = {
    label: 'milestone.form.beneficiary',
    variant: FieldVariant.TEXT,
    isRequired: false,
    isDisabled,
    error: store.getNestedObservableError('beneficiaire'),
    props: {
      placeholder: 'milestone.form.placeholders.beneficiary',
      value: store.getNestedObservable('milestoneDto', 'beneficiaire'),
      onChange: (value: string) => {
        store.setNested('milestoneDto', 'beneficiaire', value);
        store.clearError('beneficiaire');
      },
    },
  };

  const labelField: DynamicField<TextFieldProps> = {
    label: 'milestone.form.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservableError('realisationEnvisagees'),
    isDisabled,
    props: {
      value: store.getNestedObservable<string>('milestoneDto', 'realisationEnvisagees'),
      onChange: (value: string) => {
        store.setNested('milestoneDto', 'realisationEnvisagees', value);
        store.clearError('realisationEnvisagees');
      },
      placeholder: 'milestone.form.placeholders.label',
    },
  };

  const typeOfMarketField: DynamicField<SelectFieldProps> = {
    label: 'milestone.form.marketType',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservableError('typeMarcher.id', false),
    isDisabled,
    props: {
      value: store.getNestedObservable<SelectOption>('typeOfMarketIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('milestoneDto', 'typeMarcher.id', value.code, false);
        store.setNested('typeOfMarketIdSelected', value);
        store.setNested('milestoneDto', 'modePassation.id', undefined, false);
        store.setNested('selectionModeIdSelected', undefined);
        store.clearError('typeMarcher.id', false);
      },
      placeholder: 'milestone.form.placeholders.market_type',
      options: marketTypesOptions,
    },
  };

  const isSelectionModeDisabled = combineLatest([
    isDisabled instanceof Observable ? isDisabled : of(false),
    store?.getNestedObservable<string>('milestoneDto', 'typeMarcher.id', false) ?? of(null),
  ]).pipe(map(([disabled, typeMarcherId]) => disabled || !typeMarcherId));

  const selectionModeField: DynamicField<SelectFieldProps> = {
    label: 'milestone.form.selection_mode',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled: isSelectionModeDisabled,
    error: store.getNestedObservableError('modePassation.id', false),
    props: {
      value: store.getNestedObservable<SelectOption>('selectionModeIdSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('milestoneDto', 'modePassation.id', value.code, false);
        store.setNested('selectionModeIdSelected', value);
        store.clearError('modePassation.id', false);
      },
      placeholder: 'milestone.form.placeholders.selection_mode',
      options: selectionModeOptions,
    },
  };

  const budgetEstimatedField: DynamicField<CustomFieldProps> = {
    label: 'milestone.form.estimated_budget',
    variant: FieldVariant.CUSTOM,
    props: {
      component: BudgetEstimateComponent,
      value: combineLatest([
        store.getNestedObservable<FundingDto[]>('milestoneDto', 'financements'),
        of(currencies), // or an observable of currencies if dynamic
      ]).pipe(
        map(([list, currencies]) => {
          const result = list.reduce((sum, f) => {
            const currency = currencies?.find(c => c.id === f.monnaieId);
            const amount = f.budgetEstimer || 0;
            const taux = currency?.taux || 1;
            return sum + amount * taux;
          }, 0);
          store.setNested('milestoneDto.budgetEstime', result);
          return result;
        })
      ),
      injectKeys: ['label', 'value'],
    },
  };

  const datePresumedLaunchField: DynamicField<DateFieldProps> = {
    label: 'milestone.form.date_prev_lps',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservableError('datePrevLps'),
    props: {
      value: store.getNestedObservable<Date>('milestoneDto', 'datePrevLps'),
      onChange: (value: Date) => {
        store.setNested('milestoneDto', 'datePrevLps', value);
        store.clearError('datePrevLps');
      },
    },
  };
  const datePresumedAchievementField: DynamicField<DateFieldProps> = {
    label: 'milestone.form.date_prev_ac',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservableError('datePrevAc'),
    props: {
      value: store.getNestedObservable<Date>('milestoneDto', 'datePrevAc'),
      onChange: (value: Date) => {
        store.setNested('milestoneDto', 'datePrevAc', value);
        dateUtils.normalizeDate(value);
        store.clearError('datePrevAc');
      },
    },
  };
  const startDateField: DynamicField<DateFieldProps> = {
    label: 'milestone.form.date_prev_dp',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservableError('datePrevDp'),
    props: {
      value: store.getNestedObservable<Date>('milestoneDto', 'datePrevDp'),
      onChange: (value: Date) => {
        store.setNested('milestoneDto', 'datePrevDp', value);
        store.clearError('datePrevDp');
      },
    },
  };
  const endDateField: DynamicField<DateFieldProps> = {
    label: 'milestone.form.date_prev_ap',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservableError('datePrevAp'),
    props: {
      value: store.getNestedObservable<Date>('milestoneDto', 'datePrevAp'),
      onChange: (value: Date) => {
        store.setNested('milestoneDto', 'datePrevAp', value);
        store.clearError('datePrevAp');
      },
    },
  };

  const isSlotsHidden = combineLatest([
    isDisabled instanceof Observable ? isDisabled : of(false),
    store.getNestedObservable<SlotDto[]>('milestoneDto.lots'),
  ]).pipe(map(([disabled, lots]) => disabled && (!lots || lots.length === 0)));

  const slotsField: DynamicField<CustomFieldProps> = {
    label: 'milestone.form.slots',
    variant: FieldVariant.CUSTOM,
    isDisabled,
    isHidden: isSlotsHidden,
    props: {
      component: PpmSlotsFormComponent,
      errors: store.getObservable('slotErrors'),
      value: store.getNestedObservable<SlotDto[]>('milestoneDto.lots'),
      disabled: isDisabled,
      onChange: (value: { description: string; budget: number | null }[]) => {
        const lots = store.getNested<SlotDto[]>('milestoneDto.lots') ?? [];

        lots.forEach((_, index) => {
          store.removeSlotError(index, 'budget');
        });

        const lotsClone = lots.map(lot => ({ ...lot }));

        const updatedLots = value.map((slot, index) => {
          const original = lotsClone[index];

          if (original) {
            if (original.description !== slot.description) {
              store.removeSlotError(index, 'description');
            }
          }

          return original?.id
            ? {
                id: original.id,
                description: slot.description,
                budget: slot.budget || undefined,
              }
            : {
                description: slot.description,
                budget: slot.budget || undefined,
              };
        });
        store.setNested('milestoneDto.lots', updatedLots);
      },
      injectKeys: ['value', 'onChange', 'disabled', 'errors'],
    },
  };

  const isFundingHidden = combineLatest([
    isDisabled instanceof Observable ? isDisabled : of(false),
    store.getNestedObservable<FundingDto[]>('milestoneDto.financements'),
  ]).pipe(map(([disabled, fundings]) => disabled && (!fundings || fundings.length === 0)));

  const fundingSourcesField: DynamicField<CustomFieldProps> = {
    label: 'milestone.form.funding',
    variant: FieldVariant.CUSTOM,
    isDisabled,
    isHidden: isFundingHidden,
    props: {
      component: PpmFundingSourceFormComponent,
      errors: store.getObservable('fundingErrors'),
      currencyOptions: currencyOptions,
      financialSourcesOptions: financialSourcesOptions,
      disabled: isDisabled,
      value: store.getNestedObservable<FundingDto[]>('milestoneDto.financements'),
      onChange: (value: FundingDto[]) => {
        const lots = store.getNested<SlotDto[]>('milestoneDto.lots') ?? [];

        lots.forEach((_, index) => {
          store.removeSlotError(index, 'budget');
        });

        const fundings = store.getNested<FundingDto[]>('milestoneDto.financements') ?? [];

        const fundingClone = fundings.map(fund => ({ ...fund }));

        const updatedFundings = value.map((fund, index) => {
          const original = fundingClone[index];

          if (original) {
            if (original.monnaieId !== fund.monnaieId) {
              store.removeFundingError(index, 'monnaieId');
            }
            if (original.sourceFinancement.id !== fund.sourceFinancement.id) {
              store.removeFundingError(index, 'sourceFinancement');
            }
            if (original.budgetEstimer !== fund.budgetEstimer) {
              store.removeFundingError(index, 'budgetEstimer');
            }
          }

          //this block has to be optimized
          return original?.id
            ? ({
                id: original.id,
                budgetEstimer: fund.budgetEstimer,
                sourceFinancement: fund.sourceFinancement,
                monnaieId: fund.monnaieId,
                examenPrealable: fund.examenPrealable,
              } satisfies FundingDto)
            : {
                budgetEstimer: fund.budgetEstimer,
                sourceFinancement: fund.sourceFinancement,
                monnaieId: fund.monnaieId,
                examenPrealable: fund.examenPrealable,
              };
        });
        store.setNested('milestoneDto.financements', updatedFundings);
      },
      globalError: store.getNestedObservable<string>('milestoneDto.errors.financements'),
      injectKeys: ['value', 'onChange', 'disabled', 'errors', 'currencyOptions', 'financialSourcesOptions', 'globalError'],
    },
  };

  //comment

  const notesField: DynamicField<TextareaFieldProps> = {
    label: 'milestone.form.comment',
    variant: FieldVariant.TEXTAREA,
    isRequired: false,
    isDisabled,
    isHidden: actualizedCount?.pipe(map(value => value === 0)),
    error: store.getNestedObservableError('comment'),
    props: {
      value: store.getNestedObservable<string>('milestoneDto.comment'),
      onChange: (value: string) => {
        store.setNested('milestoneDto.comment', value);
        store.clearError('comment');
      },
      placeholder: 'milestone.form.placeholders.comment',
      rows: 10,
      resize: 'none',
    },
  };

  return {
    title: 'Milestone Form',
    description: 'Milestone Form Description',
    isHeaderHidden: true,
    class: 'px-4',
    orientation: 'horizontal',
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [labelField, beneficiaryField],
          },
          {
            fields: [typeOfMarketField, selectionModeField],
          },
          {
            fields: [datePresumedLaunchField, datePresumedAchievementField],
          },
          {
            fields: [startDateField, endDateField],
          },
          {
            fields: [fundingSourcesField],
          },
          {
            fields: [getFieldBuilderObjectFactory(), budgetEstimatedField],
          },
          { fields: [slotsField] },
          { fields: [notesField] },
        ],
      },
    ],
  };
};
